import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyConsultationPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('login'); // login, otp, dashboard
  const [identifierType, setIdentifierType] = useState('customer_id');
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [testOtp, setTestOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Customer data after authentication
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [allAnusthans, setAllAnusthans] = useState([]);
  const [stats, setStats] = useState(null);

  const handleSendOtp = async () => {
    if (!identifier) {
      setError(identifierType === 'customer_id' ? 'Please enter your Customer ID' : 'Please enter your Mobile Number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/my-consultation/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier,
          identifier_type: identifierType
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStep('otp');
        if (data.test_otp) {
          setTestOtp(data.test_otp);
        }
      } else {
        setError(data.detail || 'No customer found with this identifier');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        document.getElementById(`consult-otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/my-consultation/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier,
          otp: otpCode
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setCustomer(data.customer);
        setOrders(data.orders || []);
        setAllAnusthans(data.all_anusthans || []);
        setStats(data.stats || null);
        setStep('dashboard');
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleParticipate = (anusthan, parentOrderId = null) => {
    // Store customer info for anusthan booking
    sessionStorage.setItem('customer_id', customer.id);
    sessionStorage.setItem('customer_name', customer.name);
    sessionStorage.setItem('customer_phone', customer.phone);
    if (parentOrderId) {
      sessionStorage.setItem('parent_order_id', parentOrderId);
    }
    navigate('/anusthan', { state: { selectedCeremony: anusthan, customerInfo: { name: customer.name, phone: customer.phone } } });
  };

  const handleLogout = () => {
    setStep('login');
    setCustomer(null);
    setOrders([]);
    setStats(null);
    setOtp(['', '', '', '', '', '']);
    setIdentifier('');
    setError('');
  };

  // Login Step
  if (step === 'login') {
    return (
      <div data-testid="my-consultation-page">
        {/* Hero */}
        <div className="page-hero" data-testid="consultation-hero">
          <div className="page-hero-content">
            <span className="page-hero-label">My Consultation</span>
            <h1 className="page-hero-title">Your ritual.<br/><em>Your proof.</em></h1>
            <p className="page-hero-sub">Access your consultation history, view Astro Analysis reports, and participate in recommended Anusthans</p>
          </div>
        </div>

        {/* Login Section */}
        <div style={{ background: '#2E221A', padding: '60px 0 80px' }}>
          <div className="wrap">
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ background: '#E5D3A8', padding: '40px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#725b47', marginBottom: '12px' }}>
                    Login Using
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => { setIdentifierType('customer_id'); setIdentifier(''); setError(''); }}
                      data-testid="login-type-customer-id"
                      style={{
                        flex: 1, padding: '12px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem',
                        background: identifierType === 'customer_id' ? '#8B4513' : 'transparent',
                        color: identifierType === 'customer_id' ? '#FAF7F3' : '#4A3324',
                        border: '1px solid #8B4513', cursor: 'pointer'
                      }}
                    >
                      Customer ID
                    </button>
                    <button
                      onClick={() => { setIdentifierType('phone'); setIdentifier(''); setError(''); }}
                      data-testid="login-type-phone"
                      style={{
                        flex: 1, padding: '12px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem',
                        background: identifierType === 'phone' ? '#8B4513' : 'transparent',
                        color: identifierType === 'phone' ? '#FAF7F3' : '#4A3324',
                        border: '1px solid #8B4513', cursor: 'pointer'
                      }}
                    >
                    Mobile Number
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#725b47', display: 'block', marginBottom: '8px' }}>
                  {identifierType === 'customer_id' ? 'Enter Customer ID' : 'Enter Mobile Number'}
                </label>
                <input
                  type={identifierType === 'phone' ? 'tel' : 'text'}
                  value={identifier}
                  onChange={(e) => setIdentifier(identifierType === 'phone' ? e.target.value.replace(/\D/g, '') : e.target.value.toUpperCase())}
                  placeholder={identifierType === 'customer_id' ? 'SRI-CXXXXX' : '10-digit mobile number'}
                  maxLength={identifierType === 'phone' ? 10 : 11}
                  data-testid="my-consultation-identifier"
                  style={{
                    width: '100%', padding: '14px 16px', fontFamily: 'Lato, sans-serif', fontSize: '0.95rem',
                    border: '1px solid rgba(139,69,19,0.3)', background: '#FAF7F3', color: '#2E221A', outline: 'none'
                  }}
                />
              </div>

              {error && (
                <div style={{ padding: '12px', background: 'rgba(211,47,47,0.1)', border: '1px solid rgba(211,47,47,0.3)', marginBottom: '20px', textAlign: 'center' }}>
                  <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#C62828' }}>{error}</span>
                </div>
              )}

              <button
                onClick={handleSendOtp}
                disabled={loading || !identifier}
                data-testid="my-consultation-send-otp"
                style={{
                  width: '100%', padding: '16px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', letterSpacing: '0.15em', textTransform: 'uppercase',
                  background: (loading || !identifier) ? '#a48c6f' : '#8B4513', color: '#FAF7F3', border: 'none', cursor: (loading || !identifier) ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Sending OTP...' : 'Send OTP to WhatsApp'}
              </button>

              <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.75rem', color: '#725b47', marginTop: '16px', textAlign: 'center' }}>
                Your Customer ID was shared with you after your first successful payment
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // OTP Step
  if (step === 'otp') {
    return (
      <div data-testid="my-consultation-otp" style={{ minHeight: '100vh', background: '#2E221A' }}>
        <div className="wrap" style={{ paddingTop: '80px', paddingBottom: '80px' }}>
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: '2rem', color: '#F0EAD6', letterSpacing: '0.08em', marginBottom: '16px' }}>
                VERIFY OTP
              </h1>
              <p style={{ fontFamily: 'IM Fell English, serif', fontSize: '1rem', color: 'rgba(240,234,214,0.6)', fontStyle: 'italic' }}>
                Enter the 6-digit code sent to your WhatsApp
              </p>
            </div>

            <div style={{ background: '#E5D3A8', padding: '40px' }}>
              {testOtp && (
                <div style={{ padding: '14px', background: 'rgba(139,69,19,0.1)', border: '1px dashed #8B4513', marginBottom: '24px', textAlign: 'center' }}>
                  <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#8B4513' }}>🧪 Test Mode OTP: <strong>{testOtp}</strong></span>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '24px' }}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    id={`consult-otp-${i}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    data-testid={`my-consultation-otp-${i}`}
                    style={{
                      width: '50px', height: '56px', textAlign: 'center', fontFamily: 'Cinzel, serif', fontSize: '1.4rem',
                      border: '1px solid rgba(139,69,19,0.3)', background: '#FAF7F3', color: '#2E221A', outline: 'none'
                    }}
                  />
                ))}
              </div>

              {error && (
                <div style={{ padding: '12px', background: 'rgba(211,47,47,0.1)', border: '1px solid rgba(211,47,47,0.3)', marginBottom: '20px', textAlign: 'center' }}>
                  <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#C62828' }}>{error}</span>
                </div>
              )}

              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.join('').length !== 6}
                data-testid="my-consultation-verify-otp"
                style={{
                  width: '100%', padding: '16px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', letterSpacing: '0.15em', textTransform: 'uppercase',
                  background: (loading || otp.join('').length !== 6) ? '#a48c6f' : '#8B4513', color: '#FAF7F3', border: 'none', cursor: (loading || otp.join('').length !== 6) ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </button>

              <button
                onClick={() => { setStep('login'); setOtp(['', '', '', '', '', '']); setError(''); }}
                style={{ width: '100%', padding: '12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: 'transparent', color: '#8B4513', border: 'none', cursor: 'pointer', marginTop: '16px' }}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to format date/time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to get Astro Analysis Status display text
  const getAstroAnalysisStatusDisplay = (order) => {
    if (order.service_type !== 'astro_analysis') return null;
    
    const status = order.astro_analysis_status;
    if (!status || status === 'not_started') {
      return { text: 'Analysis Not Started', color: '#E65100', bg: 'rgba(255,152,0,0.15)' };
    } else if (status === 'in_progress') {
      return { text: 'Analysis In Progress', color: '#1565C0', bg: 'rgba(33,150,243,0.15)' };
    } else if (status === 'done' || status === 'completed') {
      return { text: 'Astro Analysis is Done', color: '#2E7D32', bg: 'rgba(76,175,80,0.15)' };
    }
    return { text: status, color: '#725b47', bg: 'rgba(0,0,0,0.05)' };
  };

  // Helper to get service type display name
  const getServiceDisplayName = (serviceType) => {
    switch (serviceType) {
      case 'astro_analysis':
        return 'Astro Analysis';
      case 'anusthan':
        return 'Anusthan';
      case 'bhoj_daan':
        return 'Bhoj & Daan';
      default:
        return serviceType?.replace(/_/g, ' ') || '-';
    }
  };

  // Get recommended anusthans with status "Recommended" or "pending"
  const getRecommendedAnusthans = (order) => {
    if (order.service_type !== 'astro_analysis') return [];
    if (!order.recommendations || order.recommendations.length === 0) return [];
    // Return recommendations with status "recommended" or "pending"
    return order.recommendations.filter(rec => 
      rec.status === 'recommended' || rec.status === 'pending' || rec.status === 'Recommended'
    );
  };

  // Dashboard Step
  if (step === 'dashboard' && customer) {
    return (
      <div data-testid="my-consultation-dashboard" style={{ minHeight: '100vh', background: '#FAF7F3' }}>
        {/* Header */}
        <div style={{ background: '#2E221A', padding: '40px 0' }}>
          <div className="wrap">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D4A843', marginBottom: '8px' }}>
                  Welcome Back
                </div>
                <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.8rem', color: '#F0EAD6', letterSpacing: '0.05em', margin: 0 }}>
                  {customer.name}
                </h1>
                <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#D4A843', marginTop: '8px', background: 'rgba(212,168,67,0.15)', padding: '6px 12px', display: 'inline-block' }}>
                  {customer.id}
                </div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="my-consultation-logout"
                style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', background: 'transparent', color: '#D4A843', border: '1px solid #D4A843', padding: '10px 20px', cursor: 'pointer' }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        <div className="wrap" style={{ padding: '48px 16px' }}>
          {/* Customer Summary Card */}
          {stats && (
            <CustomerSummaryCard stats={stats} customerName={customer.name} />
          )}

          {/* Orders Table Section */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C05C34', marginBottom: '16px' }}>
              YOUR ORDER HISTORY
            </div>
            
            {orders.length === 0 ? (
              <div style={{ padding: '48px', background: '#fff', border: '1px solid #e0d5c5', textAlign: 'center' }}>
                <span style={{ fontSize: '3rem', display: 'block', marginBottom: '16px' }}>📋</span>
                <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#8B4513' }}>No Orders Yet</div>
                <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#725b47', marginTop: '12px' }}>
                  Your consultation orders will appear here after payment.
                </p>
              </div>
            ) : (
              <div style={{ background: '#fff', border: '1px solid #e0d5c5', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1100px' }}>
                  <thead>
                    <tr style={{ background: '#f5efe6' }}>
                      <th style={thStyle}>Customer ID</th>
                      <th style={thStyle}>Order ID</th>
                      <th style={thStyle}>Order Date/Time</th>
                      <th style={thStyle}>Service</th>
                      <th style={thStyle}>Sub Service Type</th>
                      <th style={thStyle}>Recommendation</th>
                      <th style={thStyle}>Astro Analysis Status</th>
                      <th style={thStyle}>Astro Analysis Document</th>
                      <th style={thStyle}>Video URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => {
                      const isAstroAnalysis = order.service_type === 'astro_analysis';
                      const astroStatus = getAstroAnalysisStatusDisplay(order);
                      const recommendedAnusthans = getRecommendedAnusthans(order);
                      
                      return (
                        <tr 
                          key={order.id}
                          style={{ borderBottom: '1px solid #e0d5c5' }}
                          data-testid={`order-row-${order.id}`}
                        >
                          {/* Customer ID */}
                          <td style={tdStyle}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#8B4513', fontWeight: '600' }}>
                              {customer.id}
                            </span>
                          </td>
                          
                          {/* Order ID */}
                          <td style={tdStyle}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#4A3324' }}>
                              {order.id}
                            </span>
                          </td>
                          
                          {/* Order Date/Time */}
                          <td style={tdStyle}>
                            <span style={{ fontSize: '0.8rem', color: '#4A3324' }}>
                              {formatDateTime(order.created_at)}
                            </span>
                          </td>
                          
                          {/* Service */}
                          <td style={tdStyle}>
                            <span style={{ 
                              padding: '4px 10px', 
                              fontSize: '0.75rem', 
                              fontWeight: '500',
                              background: isAstroAnalysis ? 'rgba(139,69,19,0.1)' : 'rgba(156,39,176,0.1)',
                              color: isAstroAnalysis ? '#8B4513' : '#7B1FA2',
                              textTransform: 'capitalize'
                            }}>
                              {getServiceDisplayName(order.service_type)}
                            </span>
                          </td>
                          
                          {/* Sub Service Type - Blank for Astro Analysis */}
                          <td style={tdStyle}>
                            {isAstroAnalysis ? (
                              <span style={{ color: '#a48c6f' }}>—</span>
                            ) : order.service_type === 'bhoj_daan' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {(order.sub_service_types || []).map((type, idx) => (
                                  <span key={idx} style={{ fontSize: '0.75rem', color: '#00695C', background: 'rgba(0,105,92,0.1)', padding: '2px 6px', display: 'inline-block' }}>
                                    {type}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ fontSize: '0.8rem', color: '#4A3324' }}>
                                {order.anusthan_name || order.package_name || '—'}
                              </span>
                            )}
                          </td>
                          
                          {/* Recommendation - Only visible for Astro Analysis */}
                          <td style={tdStyle}>
                            {isAstroAnalysis ? (
                              recommendedAnusthans.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {recommendedAnusthans.map((rec, idx) => {
                                    const anusthan = allAnusthans.find(a => a.id === rec.anusthan_id);
                                    return (
                                      <span 
                                        key={idx}
                                        onClick={() => {
                                          if (anusthan) {
                                            handleParticipate(anusthan, order.id);
                                          }
                                        }}
                                        style={{ 
                                          color: anusthan ? '#8B4513' : '#725b47',
                                          textDecoration: anusthan ? 'underline' : 'none',
                                          cursor: anusthan ? 'pointer' : 'default',
                                          fontWeight: '500',
                                          fontSize: '0.8rem'
                                        }}
                                        data-testid={`recommendation-link-${idx}`}
                                      >
                                        {rec.anusthan_name}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span style={{ color: '#a48c6f', fontSize: '0.8rem' }}>
                                  {order.recommendations_na ? 'N/A' : 'Pending'}
                                </span>
                              )
                            ) : (
                              <span style={{ color: '#a48c6f' }}>—</span>
                            )}
                          </td>
                          
                          {/* Astro Analysis Status */}
                          <td style={tdStyle}>
                            {isAstroAnalysis && astroStatus ? (
                              <span style={{ 
                                padding: '4px 10px', 
                                fontSize: '0.75rem', 
                                background: astroStatus.bg, 
                                color: astroStatus.color,
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}>
                                {astroStatus.text}
                              </span>
                            ) : (
                              <span style={{ color: '#a48c6f' }}>—</span>
                            )}
                          </td>
                          
                          {/* Astro Analysis Document */}
                          <td style={tdStyle}>
                            {isAstroAnalysis ? (
                              astroStatus && (astroStatus.text === 'Astro Analysis is Done') && order.astro_analysis_file ? (
                                <a 
                                  href={`${API_URL}/api${order.astro_analysis_file}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: '#2E7D32', 
                                    textDecoration: 'none', 
                                    fontWeight: '500',
                                    fontSize: '0.8rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  data-testid={`download-document-${order.id}`}
                                >
                                  📄 Download
                                </a>
                              ) : (
                                <span style={{ color: '#FF9800', fontSize: '0.8rem' }}>
                                  Not Available now
                                </span>
                              )
                            ) : (
                              <span style={{ color: '#a48c6f' }}>—</span>
                            )}
                          </td>
                          
                          {/* Video URL - Only for non-Astro Analysis */}
                          <td style={tdStyle}>
                            {!isAstroAnalysis ? (
                              (order.anusthan_video_url || order.video_url) ? (
                                <a 
                                  href={order.anusthan_video_url || order.video_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: order.service_type === 'bhoj_daan' ? '#00695C' : '#C62828', 
                                    textDecoration: 'none', 
                                    fontWeight: '500',
                                    fontSize: '0.8rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  data-testid={`video-url-${order.id}`}
                                >
                                  🎬 Watch
                                </a>
                              ) : (
                                <span style={{ color: '#FF9800', fontSize: '0.8rem' }}>
                                  Not Available now
                                </span>
                              )
                            ) : (
                              <span style={{ color: '#a48c6f' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Available Anusthans Section */}
          <div style={{ marginTop: '48px' }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C05C34', marginBottom: '12px' }}>
                UPCOMING CEREMONIES
              </div>
              <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.6rem', color: '#2E221A', letterSpacing: '0.05em', margin: 0 }}>
                Participate in Sacred Anusthans
              </h2>
            </div>

            {allAnusthans.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
                {allAnusthans.slice(0, 6).map(anusthan => (
                  <AnusthanCard key={anusthan.id} anusthan={anusthan} onParticipate={() => handleParticipate(anusthan)} />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '48px', background: '#fff', border: '1px solid #e0d5c5' }}>
                <span style={{ fontSize: '3rem', display: 'block', marginBottom: '16px' }}>🪔</span>
                <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#8B4513' }}>
                  No Upcoming Anusthans
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Customer Summary Card Component
const CustomerSummaryCard = ({ stats, customerName }) => {
  const formatMemberSince = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amountInPaise) => {
    const rupees = (amountInPaise || 0) / 100;
    return `₹${rupees.toLocaleString('en-IN')}`;
  };

  return (
    <div data-testid="customer-summary-card" style={{
      background: 'linear-gradient(135deg, #2E221A 0%, #4A3324 100%)',
      border: '1px solid #D4A843',
      borderRadius: '4px',
      padding: '32px',
      marginBottom: '32px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative element */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '120px',
        height: '120px',
        background: 'radial-gradient(circle, rgba(212,168,67,0.15) 0%, transparent 70%)',
        borderRadius: '50%'
      }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D4A843', marginBottom: '8px' }}>
            Your Journey with Amatya
          </div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.4rem', color: '#F0EAD6', margin: 0, letterSpacing: '0.05em' }}>
            Welcome, {customerName?.split(' ')[0]}
          </h2>
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
          {/* Total Orders */}
          <div style={{ textAlign: 'center', padding: '20px', background: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.3)' }}>
            <div style={{ fontFamily: 'Cinzel, serif', fontSize: '2rem', color: '#D4A843', marginBottom: '8px', lineHeight: 1 }}>
              {stats.total_orders || 0}
            </div>
            <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(240,234,214,0.7)' }}>
              Total Orders
            </div>
          </div>

          {/* Total Spent */}
          <div style={{ textAlign: 'center', padding: '20px', background: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.3)' }}>
            <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.6rem', color: '#D4A843', marginBottom: '8px', lineHeight: 1 }}>
              {formatCurrency(stats.total_spent)}
            </div>
            <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(240,234,214,0.7)' }}>
              Total Spent
            </div>
          </div>

          {/* Member Since */}
          <div style={{ textAlign: 'center', padding: '20px', background: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.3)' }}>
            <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: '#D4A843', marginBottom: '8px', lineHeight: 1.3 }}>
              {formatMemberSince(stats.member_since)}
            </div>
            <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(240,234,214,0.7)' }}>
              Member Since
            </div>
          </div>
        </div>

        {/* Gratitude Message */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ fontFamily: 'IM Fell English, serif', fontSize: '0.95rem', color: 'rgba(240,234,214,0.6)', fontStyle: 'italic', margin: 0 }}>
            "Thank you for trusting us with your celestial journey"
          </p>
        </div>
      </div>
    </div>
  );
};

// Anusthan Card Component
const AnusthanCard = ({ anusthan, onParticipate }) => (
  <div style={{ background: '#f3e8d1', border: '1px solid #725b47', overflow: 'hidden' }}>
    <div style={{ background: '#f3e8d1', padding: '32px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: '3.5rem' }}>{anusthan.icon}</span>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', background: '#725b47', color: '#e8e0d5' }}>
      <span style={{ fontFamily: 'Cinzel, serif', fontSize: '0.8rem', fontWeight: '500', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{anusthan.displayDate || anusthan.display_date}</span>
      <span style={{ fontFamily: 'IM Fell English, serif', fontSize: '0.85rem', fontStyle: 'italic', color: '#d0c8bb' }}>{anusthan.tithiLabel || anusthan.tithi_label}</span>
    </div>
    <div style={{ padding: '20px' }}>
      <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c05c34', display: 'block', marginBottom: '8px' }}>{anusthan.deity}</span>
      <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#4a3324', letterSpacing: '0.03em', marginBottom: '12px', lineHeight: '1.3' }}>{anusthan.name}</div>
      <p style={{ fontFamily: 'IM Fell English, serif', fontSize: '0.85rem', lineHeight: '1.6', color: '#725b47', marginBottom: '16px' }}>{anusthan.shortDesc || anusthan.short_desc}</p>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid #a48c6f', background: '#f3e8d1' }}>
      <div>
        <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.6rem', color: '#725b47', display: 'block' }}>From</span>
        <span style={{ fontFamily: 'Cinzel, serif', fontSize: '0.95rem', color: '#4a3324' }}>₹ {anusthan.packages?.[0]?.price?.toLocaleString()}</span>
      </div>
      <button
        onClick={onParticipate}
        style={{
          fontFamily: 'Lato, sans-serif', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase',
          background: '#8B4513', color: '#FAF7F3', border: 'none', padding: '10px 20px', cursor: 'pointer'
        }}
      >
        Participate →
      </button>
    </div>
  </div>
);

// Table styles - Updated v2
const thStyle = { padding: '12px 14px', textAlign: 'left', fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#725b47', fontWeight: '600', whiteSpace: 'nowrap' };
const tdStyle = { padding: '12px 14px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#2E221A', verticalAlign: 'middle' };

export default MyConsultationPage;
